import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Crown, TrendingUp, Ticket, CreditCard, CalendarCheck, Receipt, Search, Bell, Calendar, Plus, ArrowRight, Clock, QrCode, UserPlus, FileBarChart, Zap } from "lucide-react";
import { api, money, fmtDate } from "../../lib/api";

const gold = "#D4AF37";
const purple = "#9D4EDD";

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  return `${Math.floor(s / 86400)} day${Math.floor(s / 86400) !== 1 ? "s" : ""} ago`;
};

const Spark = ({ data, color }) => {
  const vals = (data && data.length ? data : [3, 5, 4, 6, 5, 7, 6, 9]).slice(-12);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${28 - ((v - min) / (max - min || 1)) * 24 - 2}`).join(" ");
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="w-20 h-7">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </svg>
  );
};

const StatCard = ({ icon: Icon, label, value, delta, accent, spark, tid }) => (
  <div data-testid={tid} className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
    <div className="flex items-start justify-between">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}1f` }}><Icon size={22} style={{ color: accent }} /></div>
      <Spark data={spark} color={accent} />
    </div>
    <p className="text-[#A19DB0] text-[11px] uppercase tracking-wider mt-3">{label}</p>
    <p className="font-display text-3xl font-bold mt-1">{value}</p>
    {delta != null && <p className="text-[#4ADE80] text-xs mt-1 flex items-center gap-1">▲ {delta}</p>}
  </div>
);

const MiniCard = ({ icon: Icon, label, value, caption, accent, onClick, tid }) => {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} data-testid={tid} className={`text-left rounded-2xl bg-white/[0.04] border border-white/10 p-5 flex items-center gap-4 ${onClick ? "hover:border-[#D4AF37]/40 transition-colors w-full" : ""}`}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1f` }}><Icon size={22} style={{ color: accent }} /></div>
      <div className="min-w-0">
        <p className="text-[#A19DB0] text-[11px] uppercase tracking-wider">{label}</p>
        <p className="font-display text-2xl font-bold leading-tight">{value}</p>
        <p className="text-[#A19DB0] text-xs">{caption}</p>
      </div>
    </Cmp>
  );
};

export default function AdminDashboard() {
  const [s, setS] = useState({});
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => setS({}));
    api.get("/admin/purchases").then((r) => setOrders(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const rbd = s.revenue_by_day || [];
  const sparkVals = rbd.map((d) => d.value);
  const maxBar = Math.max(...sparkVals, 1);
  const todayStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[#A19DB0] text-sm">Welcome back,</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight">Darkchata Admin</h1>
          <p className="text-[#A19DB0] text-sm tracking-[0.25em] mt-1">Manage &nbsp;•&nbsp; Connect &nbsp;•&nbsp; Grow</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 w-72">
            <Search size={16} className="text-[#A19DB0]" />
            <input placeholder="Search members, passes, or check-ins..." className="bg-transparent outline-none text-sm w-full placeholder:text-white/30" data-testid="admin-search" />
          </div>
          <button className="relative w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center" data-testid="admin-bell">
            <Bell size={18} className="text-[#A19DB0]" />
            {s.pending_payments > 0 && <span className="absolute top-2.5 right-3 w-2 h-2 rounded-full bg-[#D4AF37]" />}
          </button>
          <div className="hidden xl:block text-right text-[#A19DB0] text-[11px] uppercase tracking-[0.25em] leading-relaxed">Good dance<br />creates<br />brighter people</div>
        </div>
      </div>

      {/* Date + quick action */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3">
          <Calendar size={18} className="text-[#D4AF37]" />
          <div><p className="text-[#A19DB0] text-[11px]">Today</p><p className="font-medium text-sm">{todayStr}</p></div>
        </div>
        <button onClick={() => navigate("/admin/scanner")} data-testid="quick-action-btn" className="flex items-center gap-2 rounded-2xl border border-[#D4AF37]/60 text-[#D4AF37] font-semibold px-5 py-3 hover:bg-[#D4AF37]/10 transition-colors"><Plus size={18} /> Quick Action</button>
      </div>

      {/* Primary stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Members" value={s.total_members ?? "—"} delta={`${s.members_delta ?? 0} this month`} accent={gold} spark={sparkVals} tid="stat-members" />
        <StatCard icon={Crown} label="Active memberships" value={s.active_memberships ?? "—"} delta={`${s.active_delta ?? 0} this month`} accent={purple} spark={sparkVals} tid="stat-active" />
        <StatCard icon={TrendingUp} label="Revenue (S$)" value={s.revenue ?? "—"} delta={`${s.revenue_delta_pct ?? 0}% this month`} accent={gold} spark={sparkVals} tid="stat-revenue" />
        <StatCard icon={Ticket} label="Passes sold" value={s.credits_sold ?? "—"} delta={`${s.passes_delta ?? 0} this month`} accent={purple} spark={sparkVals} tid="stat-credits-sold" />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <MiniCard icon={CreditCard} label="Passes used" value={s.credits_used ?? "—"} caption={`${s.usage_rate ?? 0}% usage rate`} accent={gold} tid="stat-credits-used" />
        <MiniCard icon={CalendarCheck} label="Today's check-ins" value={s.today_attendance ?? "—"} caption={s.today_attendance ? "" : "No check-ins yet"} accent={purple} tid="stat-today" />
        <MiniCard icon={Receipt} label="Pending payments" value={s.pending_payments ?? "—"} caption={s.pending_payments ? "Need review" : "All caught up! 🎉"} accent="#E57373" onClick={() => navigate("/admin/payments")} tid="stat-pending" />
      </div>

      {/* Revenue + activity */}
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-2xl bg-white/[0.04] border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg font-semibold flex items-center gap-2"><FileBarChart size={18} className="text-[#D4AF37]" /> Revenue Overview</p>
            <span className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-[#A19DB0]">Last 30 days</span>
          </div>
          <p className="font-display text-4xl font-bold mt-3">{money(s.revenue || 0)} <span className="text-[#4ADE80] text-base align-middle">↑ +{s.revenue_delta_pct ?? 0}%</span></p>
          <p className="text-[#A19DB0] text-sm">Total revenue</p>
          <div className="flex items-end gap-1 h-40 mt-5">
            {rbd.map((d, i) => (
              <div key={i} className="flex-1 rounded-t" style={{ height: `${Math.max(2, (d.value / maxBar) * 100)}%`, background: `linear-gradient(180deg,${purple},${purple}66)` }} title={`${d.day}: ${money(d.value)}`} />
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="font-display text-lg font-semibold flex items-center gap-2"><Clock size={18} className="text-[#D4AF37]" /> Recent Activity</p>
            <button onClick={() => navigate("/admin/purchases")} className="text-[#9D4EDD] text-sm flex items-center gap-1">View all <ArrowRight size={14} /></button>
          </div>
          <div className="space-y-4">
            {orders.length === 0 && <p className="text-[#A19DB0] text-sm">No recent activity.</p>}
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3" data-testid={`activity-${o.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center shrink-0"><Ticket size={16} className="text-[#D4AF37]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{o.status === "paid" ? "Payment received" : "Pass purchased"}</p>
                  <p className="text-[#A19DB0] text-xs truncate">{(o.items || []).map((i) => i.name).join(", ") || o.buyer_name}</p>
                </div>
                <span className="text-[#A19DB0] text-xs whitespace-nowrap">{ago(o.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-6">
        <p className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Zap size={18} className="text-[#D4AF37]" /> Quick Actions</p>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {[
            { label: "Scan QR Code", icon: QrCode, to: "/admin/scanner" },
            { label: "Add Member", icon: UserPlus, to: "/admin/members" },
            { label: "Create Pass", icon: Ticket, to: "/admin/packages" },
            { label: "View Calendar", icon: Calendar, to: "/admin/calendar" },
            { label: "Generate Report", icon: FileBarChart, to: "/admin/reports" },
          ].map((q) => (
            <button key={q.to} onClick={() => navigate(q.to)} data-testid={`qa-${q.to}`} className="flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4 hover:border-[#D4AF37]/40 transition-colors">
              <q.icon size={20} className="text-[#D4AF37]" /> <span className="text-sm font-medium">{q.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
